import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { statusOption, config, searchRegex } from "../../../../constant";
import { useElement, useLocale, useService } from "../../../../hooks";
import { AppContext, NavigatorContext } from "../../../../context";

export const useAdminProjectList = () => {
  const { t } = useLocale();
  const { navigateTo } = useContext(NavigatorContext);
  const Actor = useElement("Actor");
  const CheckBox = useElement("CheckBox");
  const Img = useElement("Img");
  const { fetchAll, remove, update, save } = useService("Data", "project");
  const { setTitle } = useService("Meta");
  const { actions: { showPreloader, hidePreloader } } = useContext(AppContext);
  const { isBottom, showAlert, generateUID, getFormattedDate } =
    useService("Misc");
  const { urlToSplitData, getLocationData } = useService("Router");
  const { commonPageSize } = config;
  const filterTags = statusOption;
  const [tableContent, settableContent] = useState(null);
  const [tableData, settableData] = useState([]);
  const [filteredList, setfilteredList] = useState([]);
  const [checkedList, setcheckedList] = useState([]);
  const [activeItem, setactiveItem] = useState(null);
  const [searchKey, setsearchKey] = useState<string>("");
  const [activeFilterIndex, setactiveFilterIndex] = useState<number>(0);
  const [showTrashConfirm, setshowTrashConfirm] = useState<boolean>(false);
  const [trashAction, settrashAction] = useState<boolean>(false);
  const [isMultipleDelete, setisMultipleDelete] = useState<boolean>(false);
  const [checkedAll, setcheckedAll] = useState<boolean>(false);
  const [activeShownTotal, setactiveShownTotal] =
    useState<number>(commonPageSize);
  const [activeFilter, setactiveFilter] = useState(filterTags[0]);

  const filterItems = useCallback((key, activeFilterIndex, tableData) => {
    const filteredList =
      key === "all" ? tableData : tableData.filter((e) => e.status === key);
    setfilteredList(filteredList);
    setactiveFilterIndex(activeFilterIndex);
  }, []);

  const showMore = useCallback(() => {
    showPreloader();
    setTimeout(() => {
      hidePreloader();
      setactiveShownTotal(activeShownTotal + commonPageSize);
    }, 300);
  }, [activeShownTotal, commonPageSize, hidePreloader, showPreloader]);

  const trackScrolling = useCallback(() => {
    const wrappedElement = document.getElementById("list-wrapper");
    if (isBottom(wrappedElement)) {
      if (
        filteredList.length > commonPageSize &&
        activeShownTotal < filteredList.length
      )
        showMore();
    }
  }, [
    activeShownTotal,
    commonPageSize,
    filteredList.length,
    isBottom,
    showMore,
  ]);

  const getListData = useCallback(async () => {
    const result = await fetchAll();
    if (result.status) {
      let tableData = [];
      result.data.forEach((e) => {
        if (!trashAction) {
          if (!e.trash) tableData.push(e);
        } else {
          if (e.trash) tableData.push(e);
        }
      });
      settableData(tableData);
      let activefilterValue = "all";
      filterTags.forEach((e) => {
        if (Number(e.id) === activeFilterIndex + 1) {
          activefilterValue = e.value;
        }
      });
      filterItems(activefilterValue, activeFilterIndex, tableData);
      if (result.data?.length === 0) {
        showAlert({ type: "error", msg: "No project added yet!" });
      }
    } else showAlert({ type: "error", msg: "No project added yet!" });
  }, [
    activeFilterIndex,
    fetchAll,
    filterItems,
    filterTags,
    showAlert,
    trashAction,
  ]);

  const trashCancel = () => {
    setactiveItem(null);
    setshowTrashConfirm(false);
  };

  const deleteActive = useCallback(
    async (uid, task = "trash") => {
      if (trashAction && task === "trash") {
        const result = await remove({ uid });
        if (result.status) {
          getListData();
          showAlert({ type: "success", msg: "Project deleted successfully!" });
          setactiveItem(null);
          setshowTrashConfirm(false);
          getListData();
        } else showAlert({ type: "error", msg: "Unable to delete project!" });
      } else {
        let projectObject = null;
        tableData.forEach((e) => {
          if (e.uid === uid) {
            projectObject = e;
          }
        });
        projectObject.trash = task === "trash";
        delete projectObject._id;
        let data = {
          query: JSON.stringify({ uid }),
          projectData: JSON.stringify(projectObject),
        };
        const result = await update(data);
        if (result.status) {
          showAlert({
            type: "success",
            msg: "Project " + task + "d successfully!",
          });
          setactiveItem(null);
          setshowTrashConfirm(false);
          getListData();
        } else
          showAlert({ type: "error", msg: "Unable to " + task + " project!" });
      }
    },
    [getListData, remove, showAlert, tableData, trashAction, update]
  );

  const deleteSelected = useCallback(async () => {
    deleteActive(activeItem.uid);
  }, [activeItem, deleteActive]);

  useEffect(() => {
    setcheckedAll(filteredList.length === checkedList.length);
  }, [filteredList, checkedList]);

  const toggleCheck = useCallback(
    (uid) => {
      if (checkedList.includes(uid)) {
        setcheckedList(checkedList.filter((e) => e !== uid));
      } else {
        setcheckedList([...checkedList, uid]);
      }
    },
    [checkedList, setcheckedList]
  );

  const deleteChecked = useCallback(() => {
    (checkedAll ? filteredList : checkedList).forEach((e) => {
      deleteActive(e.uid);
    });
    setcheckedList([]);
    setisMultipleDelete(false);
  }, [checkedAll, checkedList, deleteActive, filteredList]);

  const cloneProject = useCallback(
    async (item) => {
      let projectObject = {
        ...item,
        projectObject: item.slug + "-copy",
        uid: generateUID(),
      };
      const result = await save(projectObject);
      if (result.status) {
        showAlert({ type: "success", msg: "Project clonsed successfully!" });
        getListData();
      } else showAlert({ type: "error", msg: "Unable to add project!" });
    },
    [generateUID, getListData, save, showAlert]
  );

  const getTableData = useCallback(
    (filterData) => {
      let data = [];
      filterData.forEach((item) => {
        let e = {
          checkAction: (
            <span className="check-all" onClick={() => toggleCheck(item.uid)}>
              <CheckBox
                isChecked={checkedAll || checkedList.includes(item.uid)}
              />
            </span>
          ),
          title: item.title,
          image: item.image ? (
            <Img
              src={JSON.parse(item.image)[0]}
              style={{ maxHeight: 40, maxWidth: 40 }}
            />
          ) : (
            <p>{t("noImage")}</p>
          ),
          releaseDate: getFormattedDate(new Date(item.releaseDate)),
          status: item.status,
          action: trashAction ? (
            <div className="action-bar">
              <Actor
                type="trash"
                onClick={() => {
                  setactiveItem(item);
                  setshowTrashConfirm(true);
                }}
              />
              <Actor
                type="undo"
                onClick={() => deleteActive(item.uid, "restore")}
              />
            </div>
          ) : (
            <div className="action-bar">
              <Actor
                type="trash"
                onClick={() => {
                  setactiveItem(item);
                  setshowTrashConfirm(true);
                }}
              />
              <Actor
                type="view"
                onClick={() =>
                  navigateTo({ route: "/project/" + item.slug, newTab: true })
                }
              />
              <Actor
                type="edit"
                onClick={() =>
                  navigateTo({ route: "/admin/project/edit/" + item.uid })
                }
              />
              <Actor type="copy" onClick={() => cloneProject(item)} />
            </div>
          ),
        };
        data.push(e);
      });
      return data;
    },
    [
      Actor,
      CheckBox,
      Img,
      checkedAll,
      checkedList,
      cloneProject,
      deleteActive,
      getFormattedDate,
      navigateTo,
      t,
      toggleCheck,
      trashAction,
    ]
  );

  const search = useCallback(
    (e) => {
      if (e.target.value === "" || searchRegex.test(e.target.value)) {
        setsearchKey(e.target.value);
        let tempTitle = "",
          filteredList = [];
        tableData.forEach((e) => {
          tempTitle = e.title.toLowerCase();
          if (tempTitle.search(searchKey) !== -1) filteredList.push(e);
        });
        setfilteredList(filteredList);
      }
    },
    [searchKey, tableData, setfilteredList]
  );

  const resetSearch = useCallback(() => {
    setfilteredList(tableData);
    setsearchKey("");
  }, [tableData]);

  setTitle(t("projectsList"), "admin");
  const urlData = useMemo(
    () => urlToSplitData(getLocationData().pathname),
    [getLocationData, urlToSplitData]
  );
  useEffect(() => {
    settrashAction(urlData[2] === "trash");
    getListData();
  }, [getListData, urlData]);

  useEffect(() => {
    settableContent({
      headers: [
        {
          template: (
            <span
              className="check-all"
              onClick={() => {
                let newCheckedList = [];
                if (checkedAll) {
                  setcheckedAll(!checkedAll);
                  setcheckedList([]);
                } else {
                  filteredList.forEach((e) => newCheckedList.push(e.uid));
                  setcheckedAll(!checkedAll);
                  setcheckedList(newCheckedList);
                }
              }}
            >
              <CheckBox
                isChecked={
                  checkedAll ||
                  (checkedList.length === filteredList.length &&
                    filteredList.length !== 0)
                }
              />
            </span>
          ),
        },
        {
          label: t("title"),
          key: "title",
        },
        {
          label: t("image"),
          key: "image",
        },
        {
          label: "Release Date",
          key: "releaseDate",
        },
        {
          label: t("projectStatus"),
          key: "status",
        },
        {
          label: t("actions"),
          key: "actions",
        },
      ],
      content: getTableData(filteredList),
    });
  }, [filteredList, checkedAll, checkedList, CheckBox, t, getTableData]);
  return {
    trackScrolling,
    trashAction,
    checkedList,
    checkedAll,
    setisMultipleDelete,
    setshowTrashConfirm,
    activeFilter,
    setactiveFilter,
    filterItems,
    tableData,
    filterTags,
    searchKey,
    search,
    resetSearch,
    tableContent,
    activeShownTotal,
    deleteChecked,
    deleteSelected,
    trashCancel,
    showTrashConfirm,
    isMultipleDelete,
  };
};
